import React from 'react';
import { SearchDropdown } from '../../../../../components';
import { ElectricConfig } from '../../../../../types';
import { NewExistingSegment, NewExistingValue } from '../../../../../components/shared';
import { SUBPANEL_CONFIG } from '../../../../../constants/app.constants';

interface SubpanelSelectorProps {
  onSubpanelSizeSelect: (value: string) => void;
  onMainBreakerRatingSelect: (value: number) => void;
  onFeedingMethodSelect: (value: string) => void;
  onOcpdSizeSelect: (value: string) => void;
  onPvBreakerSizeSelect: (value: string) => void;
  onPvBreakerLocationSelect: (value: string) => void;
  onSubpanelNewExistingChange: (value: NewExistingValue) => void;
  onMainBreakerNewExistingChange: (value: NewExistingValue) => void;
  selectedSubpanelSize?: string;
  selectedMainBreakerRating?: number;
  selectedFeedingMethod?: string;
  selectedOcpdSize?: string;
  selectedPvBreakerSize?: string;
  selectedPvBreakerLocation?: string;
  subpanelNewExisting?: NewExistingValue;
  mainBreakerNewExisting?: NewExistingValue;
  electricConfigs: ElectricConfig[];
  isLoading: boolean;
  error?: string;
  isEnabled: boolean;
}

export const SubpanelSelector: React.FC<SubpanelSelectorProps> = ({
  onSubpanelSizeSelect,
  onMainBreakerRatingSelect,
  onFeedingMethodSelect,
  onOcpdSizeSelect,
  onPvBreakerSizeSelect,
  onPvBreakerLocationSelect,
  onSubpanelNewExistingChange,
  onMainBreakerNewExistingChange,
  selectedSubpanelSize,
  selectedMainBreakerRating,
  selectedFeedingMethod,
  selectedOcpdSize,
  selectedPvBreakerSize,
  selectedPvBreakerLocation,
  subpanelNewExisting,
  mainBreakerNewExisting,
  electricConfigs,
  isLoading,
  error,
  isEnabled
}) => {

  // Filter electric configs by type for each field
  const subpanelSizeOptions = electricConfigs?.filter(config => config.type === 'subpanel_size') || [];
  const mainBreakerSizeOptions = electricConfigs?.filter(config => config.type === 'main_breaker_size') || [];
  const feedingMethodOptions = electricConfigs?.filter(config => config.type === 'feeding_method') || [];
  const ocpdSizeOptions = electricConfigs?.filter(config => config.type === 'ocpd') || [];
  const pvBreakerSizeOptions = electricConfigs?.filter(config => config.type === 'pv_breaker_size') || [];
  const pvBreakerLocationOptions = electricConfigs?.filter(config => config.type === 'pv_breaker_location') || [];

  const handleSubpanelSizeSelect = (option: any) => {
    if (!option) {
      onSubpanelSizeSelect('');
      return;
    }
    const config = subpanelSizeOptions?.find(c => c.id === option.id);
    if (config) onSubpanelSizeSelect(config.value);
  };

  const handleMainBreakerRatingSelect = (option: any) => {
    if (!option) {
      onMainBreakerRatingSelect(0);
      return;
    }
    const config = mainBreakerSizeOptions?.find(c => c.id === option.id);
    if (config) {
      onMainBreakerRatingSelect(parseInt(config.value, 10));
    }
  };

  const handleFeedingMethodSelect = (option: any) => {
    if (!option) {
      onFeedingMethodSelect('');
      return;
    }
    const config = feedingMethodOptions?.find(c => c.id === option.id);
    if (config) onFeedingMethodSelect(config.value);
  };

  const handleOcpdSizeSelect = (option: any) => {
    if (!option) {
      onOcpdSizeSelect('');
      return;
    }
    const config = ocpdSizeOptions?.find(c => c.id === option.id);
    if (config) onOcpdSizeSelect(config.value);
  };

  const handlePvBreakerSizeSelect = (option: any) => {
    if (!option) {
      onPvBreakerSizeSelect('');
      return;
    }
    const config = pvBreakerSizeOptions?.find(c => c.id === option.id);
    if (config) onPvBreakerSizeSelect(config.value);
  };

  const handlePvBreakerLocationSelect = (option: any) => {
    if (!option) {
      onPvBreakerLocationSelect('');
      return;
    }
    const config = pvBreakerLocationOptions?.find(c => c.id === option.id);
    if (config) onPvBreakerLocationSelect(config.value);
  };

  // Convert to dropdown format
  const subpanelSizeDropdownOptions = subpanelSizeOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const mainBreakerRatingDropdownOptions = mainBreakerSizeOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const feedingMethodDropdownOptions = feedingMethodOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const ocpdSizeDropdownOptions = ocpdSizeOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const pvBreakerSizeDropdownOptions = pvBreakerSizeOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const pvBreakerLocationDropdownOptions = pvBreakerLocationOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs">Select a task to configure Subpanel details</p>
      </div>
    );
  }

  return (
    <div className="bg-transparent p-0">
      <div className="space-y-4">
      {/* Row 1: Feeding/Tie Method */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Feeding/Tie Method
          </label>
          <SearchDropdown
            options={feedingMethodDropdownOptions}
            onSelect={handleFeedingMethodSelect}
            placeholder="Choose feeding method..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedFeedingMethod ? { 
              id: feedingMethodOptions.find(opt => opt.value === selectedFeedingMethod)?.id || '', 
              label: selectedFeedingMethod 
            } : null}
            disabled={!isEnabled}
          />
        </div>
      </div>

      {/* Row 2: OCPD Size */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            OCPD Size (MSP Load Breaker Rating)
          </label>
          <SearchDropdown
            options={ocpdSizeDropdownOptions}
            onSelect={handleOcpdSizeSelect}
            placeholder="Choose OCPD size..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedOcpdSize ? { 
              id: ocpdSizeOptions.find(opt => opt.value === selectedOcpdSize)?.id || '', 
              label: selectedOcpdSize 
            } : null}
            disabled={!isEnabled || selectedFeedingMethod !== SUBPANEL_CONFIG.FEEDING_METHODS.BREAKER_IN_MSP}
          />
        </div>
      </div>

      {/* Row 3: Subpanel Size with grouped New/Existing */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Subpanel Size (Buss Rating)
              </label>
              {isEnabled && (
                <NewExistingSegment
                  value={subpanelNewExisting || null}
                  onChange={onSubpanelNewExistingChange}
                  disabled={!isEnabled}
                />
              )}
            </div>
            <SearchDropdown
              options={subpanelSizeDropdownOptions}
              onSelect={handleSubpanelSizeSelect}
              placeholder="Choose subpanel size..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              value={selectedSubpanelSize ? { 
                id: subpanelSizeOptions.find(opt => opt.value === selectedSubpanelSize)?.id || '', 
                label: selectedSubpanelSize 
              } : null}
              disabled={!isEnabled}
            />
          </div>
        </div>
      </div>

      {/* Row 4: Subpanel Main Breaker Rating */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Subpanel Main Breaker Rating
              </label>
              {isEnabled && (
                <NewExistingSegment
                  value={mainBreakerNewExisting || null}
                  onChange={onMainBreakerNewExistingChange}
                  disabled={!isEnabled}
                />
              )}
            </div>
            <SearchDropdown
              options={mainBreakerRatingDropdownOptions}
              onSelect={handleMainBreakerRatingSelect}
              placeholder="Choose main breaker rating..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              value={selectedMainBreakerRating ? { 
                id: mainBreakerSizeOptions.find(opt => parseInt(opt.value, 10) === selectedMainBreakerRating)?.id || '', 
                label: selectedMainBreakerRating.toString()
              } : null}
              disabled={!isEnabled}
            />
          </div>
        </div>
      </div>

      {/* Row 5: PV Breaker Size */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-1">
            PV Breaker Size
          </label>
          <SearchDropdown
            options={pvBreakerSizeDropdownOptions}
            onSelect={handlePvBreakerSizeSelect}
            placeholder="Choose PV breaker size..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedPvBreakerSize ? { 
              id: pvBreakerSizeOptions.find(opt => opt.value === selectedPvBreakerSize)?.id || '', 
              label: selectedPvBreakerSize 
            } : null}
            disabled={!isEnabled}
          />
        </div>
      </div>

      {/* Row 6: PV Breaker Location */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-1">
            PV Breaker Location
          </label>
          <SearchDropdown
            options={pvBreakerLocationDropdownOptions}
            onSelect={handlePvBreakerLocationSelect}
            placeholder="Choose PV breaker location..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedPvBreakerLocation ? { 
              id: pvBreakerLocationOptions.find(opt => opt.value === selectedPvBreakerLocation)?.id || '', 
              label: selectedPvBreakerLocation 
            } : null}
            disabled={!isEnabled}
          />
        </div>
      </div>
    </div>
    </div>
  );
};
